import { useEffect, useRef, type FormEvent } from "react";
import { useLocation } from "react-router";
import type { PhoneSearchControl } from "./phoneSearchState";

/**
 * Batch 18 §11.3 (Features__PhoneSearch): below 760 px the header search is a 44×44 "Search" icon button. Tapping it opens a
 * search bar over the header — a full-width 44 px field with the keyboard up and a Cancel button; the brand and the header
 * actions are hidden while it is open. Enter searches exactly as the desktop field does (a key or global ID opens the item).
 * Cancel, Esc and the browser's Back close it and give focus back to the icon.
 * Open or closed lives in the history entry (phoneSearchState.ts), so Back closes it.
 */
function Magnifier() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true" focusable="false">
      <circle cx="8.5" cy="8.5" r="5.5" fill="none" stroke="currentColor" strokeWidth="2" />
      <path d="m12.6 12.6 4.6 4.6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

export function PhoneSearchButton({ control }: { control: PhoneSearchControl }) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { open, takeClosedBySearch } = control;
  const wasOpen = useRef(open);
  // Closed by Cancel, Esc or Back: focus returns to the icon. (A search navigates away and moves focus itself.)
  useEffect(() => {
    if (wasOpen.current && !open && !takeClosedBySearch()) buttonRef.current?.focus();
    wasOpen.current = open;
  }, [open, takeClosedBySearch]);
  if (open) return null;
  return (
    <button ref={buttonRef} type="button" className="gf-phone-search-btn" aria-label="Search" title="Search" data-guide="search" onClick={control.openBar}>
      <Magnifier />
    </button>
  );
}

export function PhoneSearchBar({ control, onSearch }: { control: PhoneSearchControl; onSearch: (text: string) => void }) {
  const location = useLocation();
  const inputRef = useRef<HTMLInputElement>(null);
  // On the search page the bar opens with the current search in it.
  const initial = location.pathname === "/search" ? (new URLSearchParams(location.search).get("q") ?? "") : "";
  useEffect(() => {
    inputRef.current?.focus();
  }, []);
  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const value = new FormData(event.currentTarget).get("q");
    const text = typeof value === "string" ? value : "";
    if (!text.trim()) return;
    control.closeForSearch();
    onSearch(text);
  }
  return (
    <form className="gf-phone-searchbar" role="search" onSubmit={submit}>
      <label className="vh" htmlFor="gf-q-phone">
        Search work items
      </label>
      <input
        ref={inputRef}
        id="gf-q-phone"
        name="q"
        type="search"
        placeholder="Search, or type a key"
        autoComplete="off"
        enterKeyHint="search"
        defaultValue={initial}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            // Handled here, so no drawer or dialog underneath also closes on this Esc.
            event.preventDefault();
            control.closeBar();
          }
        }}
      />
      <button type="button" className="gf-phone-search-cancel" onClick={control.closeBar}>
        Cancel
      </button>
    </form>
  );
}
